import { inspectionStatusLabel } from "../../domain/shared/status-labels.js";
import { 
  createIcons, 
  ArrowLeft, 
  Edit3, 
  Power, 
  Trash2, 
  RotateCcw, 
  ShieldCheck, 
  FileText, 
  Layers, 
  Activity, 
  Flame, 
  Sparkles, 
  CheckCircle, 
  AlertTriangle, 
  History, 
  FileCheck, 
  Info, 
  Database,
  ExternalLink,
  Plus,
  GitBranch,
  Boxes,
  Building2,
  FolderKanban,
  MapPin
} from "lucide";
import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { PipingFormBuilder } from "../components/piping-form-builder.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { 
  Equipment, 
  EquipmentType, 
  EquipmentStatus,
  EquipmentDamageMechanism,
  EquipmentSnapshot,
  EquipmentDocument
} from "../../equipment/equipment-domain.js";
import { STANDARD_DAMAGE_MECHANISMS } from "../../equipment/equipment-domain.js";

export class AppEquipmentDetailPage extends HTMLElement {
  private equipmentId: string | null = null;
  private equipment: Equipment | null = null;
  private snapshots: EquipmentSnapshot[] = [];
  private documents: EquipmentDocument[] = [];
  private inspections: any[] = [];
  private relatedEquipments: Equipment[] = [];
  private clients: any[] = [];
  private projects: any[] = [];
  private unitAreas: any[] = [];
  private activeTab: 'IDENTIFICATION' | 'PROJECT' | 'OPERATION' | 'INTEGRITY' | 'INSPECTION' | 'DOCUMENTS' | 'HISTORY' = 'IDENTIFICATION';
  private isLoading = true;
  private errorMessage: string | null = null;
  private unsubscribe: (() => void) | null = null;
  private boundHandleMasterDataUpdated: (e: Event) => void;

  constructor() {
    super();
    this.boundHandleMasterDataUpdated = this.handleMasterDataUpdated.bind(this);
  }

  private handleMasterDataUpdated(e: Event) {
    this.loadEquipmentData();
  }

  static get observedAttributes() {
    return ['equipment-id', 'id'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if ((name === 'equipment-id' || name === 'id') && oldValue !== newValue && newValue) {
      this.equipmentId = newValue;
      this.loadEquipmentData();
    }
  }

  connectedCallback() {
    if (!this.equipmentId) {
      this.equipmentId = this.getAttribute('equipment-id') || this.getAttribute('id');
    }
    this.loadEquipmentData();
    this.unsubscribe = contextStore.subscribe(() => {
      this.loadEquipmentData();
    });
    window.addEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    window.removeEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  private async loadEquipmentData() {
    if (!this.equipmentId) return;
    if (this.equipmentId === 'new') {
      router.navigate('/equipment/new');
      return;
    }
    this.isLoading = true;
    this.errorMessage = null;
    this.render();

    try {
      const activeClientId = contextStore.get().activeClientId;
      const ctx = { ...sessionRequestContext(), activeClientId } as any;

      const [eq, allEqs, snaps, docs, cls, prjs, units] = await Promise.all([
        window.app.platform.equipmentService.getEquipment(ctx, this.equipmentId),
        window.app.platform.equipmentService.listEquipments(ctx).catch(() => []),
        window.app.platform.equipmentService.listSnapshots(ctx, this.equipmentId).catch(() => []),
        window.app.platform.equipmentService.listDocuments(ctx, this.equipmentId).catch(() => []),
        window.app.platform.adminService.listAuthorizedClients(ctx).catch(() => []),
        window.app.platform.projectService.listProjects(undefined, undefined, ctx).catch(() => []),
        window.app.platform.projectService.listUnitAreas(undefined, undefined, ctx).catch(() => [])
      ]);

      if (!eq) {
        this.errorMessage = "Equipamento não encontrado ou acesso não autorizado para este cliente.";
        this.isLoading = false;
        this.render();
        return;
      }

      this.equipment = eq;
      this.relatedEquipments = allEqs || [];
      this.snapshots = snaps || [];
      this.documents = docs || [];
      this.clients = cls || [];
      this.projects = prjs || [];
      this.unitAreas = units || [];

      // Load related inspections
      try {
        if (window.app.platform.inspectionService?.listInspections) {
          this.inspections = (await window.app.platform.inspectionService.listInspections(ctx, this.equipmentId as any)) as any[];
        }
      } catch (e) {
        this.inspections = [];
      }

      this.isLoading = false;
      this.render();
    } catch (e: any) {
      this.isLoading = false;
      this.errorMessage = e.safeMessage || e.message || "Erro ao carregar detalhes do equipamento.";
      this.render();
    }
  }

  private formatMeasurement(val?: { value: number | null; unit: string } | null): string {
    if (!val || val.value === null || val.value === undefined) return "-";
    return `${val.value} ${val.unit || ''}`.trim();
  }

  /**
   * REFERÊNCIAS CONSOLIDADAS DO SISTEMA — só para Sistema de Tubulação
   * (comando "correção da importação", itens 22-25): agrega DESENHO e
   * FLUXOGRAMA de todas as Linhas ativas do Sistema, sem duplicidade. É uma
   * visão adicional, somente leitura — a referência continua pertencendo à
   * Linha (nada é removido dela) e nada é escrito de volta no cadastro do
   * Sistema.
   */
  private buildSystemConsolidatedReferencesHtml(eq: Equipment): string {
    if (eq.equipmentType !== 'PIPING_SYSTEM') return '';
    const lines = this.relatedEquipments.filter(e =>
      e.equipmentType === 'PIPING_LINE' && String(e.pipingSystemId || '') === String(eq.id) && e.status !== 'INACTIVE' && !e.deletedAt
    );
    const drawings = new Set<string>();
    const flowDiagrams = new Set<string>();
    for (const line of lines) {
      const drawing = line.drawingReference || line.projectDrawingFlowchart;
      const flow = line.flowDiagramReference || line.projectDrawingFlowchart;
      if (drawing) drawings.add(drawing);
      if (flow) flowDiagrams.add(flow);
    }
    const sortNatural = (values: Set<string>) => [...values].sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' }));
    const sortedDrawings = sortNatural(drawings);
    const sortedFlowDiagrams = sortNatural(flowDiagrams);
    return `
      <div class="eq-info-panel" style="grid-column: 1 / -1;">
        <div class="eq-panel-title">
          <i data-lucide="layers" style="width: 18px; height: 18px;"></i> Referências Consolidadas do Sistema (${lines.length} ${lines.length === 1 ? 'Linha' : 'Linhas'})
        </div>
        <div class="eq-field-row" style="align-items: flex-start;">
          <span class="eq-field-label">Desenhos de Referência</span>
          <span class="eq-field-val">${sortedDrawings.length > 0 ? sortedDrawings.join(' · ') : '-'}</span>
        </div>
        <div class="eq-field-row" style="align-items: flex-start;">
          <span class="eq-field-label">Fluxogramas (P&ID/PFD)</span>
          <span class="eq-field-val">${sortedFlowDiagrams.length > 0 ? sortedFlowDiagrams.join(' · ') : '-'}</span>
        </div>
        <div style="font-size: 0.72rem; color: var(--color-text-disabled); margin-top: 8px;">
          Consolidação automática das referências cadastradas em cada Linha deste Sistema — cada Linha mantém sua própria referência; esta é apenas uma visão agregada, sem duplicidades.
        </div>
      </div>
    `;
  }

  private getTypeLabel(type: EquipmentType): string {
    const map: Record<string, string> = {
      'PRESSURE_VESSEL': 'Vaso de Pressão (VP)',
      'STORAGE_TANK': 'Tanque de Armazenamento (TQ)',
      'HEAT_EXCHANGER': 'Permutador de Calor (TC)',
      'BOILER': 'Caldeira (CD)',
      'FURNACE': 'Forno Industrial (FN)',
      'PIPING_SYSTEM': 'Sistema de Tubulação (ST)',
      'PIPING_LINE': 'Tubulação',
      'OTHER': 'Outro Equipamento'
    };
    return map[type] || type;
  }

  private getStatusBadge(status: EquipmentStatus): string {
    switch (status) {
      case 'ACTIVE':
        return `<span style="background: rgba(34, 197, 94, 0.15); color: var(--status-success-text); border: 1px solid rgba(34, 197, 94, 0.3); padding: 4px 10px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px;"><span style="width: 8px; height: 8px; border-radius: 50%; background: #4ade80;"></span> ATIVO</span>`;
      case 'INACTIVE':
        return `<span style="background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); padding: 4px 10px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px;"><span style="width: 8px; height: 8px; border-radius: 50%; background: var(--status-danger-solid);"></span> INATIVO</span>`;
      case 'OUT_OF_SERVICE':
        return `<span style="background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); padding: 4px 10px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px;"><span style="width: 8px; height: 8px; border-radius: 50%; background: var(--status-warning-text);"></span> FORA DE OPERAÇÃO</span>`;
      default:
        return `<span style="background: rgba(140, 140, 140, 0.15); color: var(--ia-fg-soft, #b5b5b5); padding: 4px 10px; border-radius: 6px; font-size: 0.8rem;">${status}</span>`;
    }
  }

  private render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div style="padding: 72px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: var(--color-text-muted);">
          <svg class="spinner spinner-dark spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Carregando...">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <span class="text-body-small">Carregando detalhes do ativo técnico...</span>
        </div>
      `;
      return;
    }

    if (this.errorMessage || !this.equipment) {
      this.innerHTML = `
        <div style="padding: 28px; max-width: 800px; margin: 40px auto; background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: var(--radius-sm); color: var(--status-danger-text);">
          <h2 style="margin: 0 0 12px 0; display: flex; align-items: center; gap: 8px; font-size: 1.125rem;">
            <i data-lucide="alert-triangle" style="width: 20px; height: 20px;"></i> Erro de Carregamento
          </h2>
          <p style="margin: 0 0 20px 0; font-size: 0.875rem; ;">${this.errorMessage || 'Equipamento não encontrado.'}</p>
          <button id="btn-back-to-list" class="btn btn-secondary btn-sm">
            <i data-lucide="arrow-left" style="width: 14px; height: 14px;"></i> Voltar para Lista de Equipamentos
          </button>
        </div>
      `;
      createIcons({ icons: { AlertTriangle, ArrowLeft }, nameAttr: "data-lucide", root: this });
      this.querySelector('#btn-back-to-list')?.addEventListener('click', () => router.navigate('/equipment'));
      return;
    }

    const eq = this.equipment;
    const isPiping = eq.equipmentType === 'PIPING_LINE';
    const canUpdate = contextStore.hasPermission(PermissionCode.EquipmentUpdate);
    const canDelete = contextStore.hasPermission(PermissionCode.EquipmentDelete) || canUpdate;
    const canInactivate = contextStore.hasPermission(PermissionCode.EquipmentInactivate) || canUpdate;
    const canReactivate = contextStore.hasPermission(PermissionCode.EquipmentReactivate) || canUpdate;

    // Resolve Hierarchy
    const client = this.clients.find(c => c.id === eq.clientId);
    const clientName = client ? (client.name || client.legalName) : (eq.clientId || 'Cliente não identificado');
    const project = this.projects.find(p => p.id === eq.projectId) || this.projects.find(p => p.clientId === eq.clientId);
    const projectName = project ? `${project.code} - ${project.name}` : (eq.projectId || 'Projeto não vinculado');
    const unitArea = this.unitAreas.find(u => u.id === (eq.unitAreaId || eq.areaId));
    const unitAreaName = unitArea ? `${unitArea.code} - ${unitArea.name}` : ((eq.unitAreaId || eq.areaId) || 'Unidade/Área não vinculada');

    // Resolve From / To names
    const fromEq = eq.fromEquipmentId ? this.relatedEquipments.find(e => e.id === eq.fromEquipmentId) : null;
    const toEq = eq.toEquipmentId ? this.relatedEquipments.find(e => e.id === eq.toEquipmentId) : null;
    const fromText = fromEq ? `${fromEq.tag} - ${fromEq.name}` : (eq.fromReference || '-');
    const toText = toEq ? `${toEq.tag} - ${toEq.name}` : (eq.toReference || '-');

    this.innerHTML = `
      <style>
        .eq-detail-container {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
          color: var(--color-text-primary);
          font-family: inherit;
        }
        .eq-detail-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
          border-bottom: 1px solid #27272a;
          padding-bottom: 20px;
        }
        .eq-title-tag {
          font-size: 1.8rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 6px 0;
          display: flex;
          align-items: center;
          gap: 12px;
          font-family: monospace;
        }
        .eq-subtitle {
          color: var(--color-text-disabled);
          font-size: 1rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .eq-action-bar {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }
        .btn-action {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid transparent;
        }
        .btn-action-back {
          background: var(--ia-bg-panel, #27272a);
          color: var(--color-text-primary);
          border-color: var(--ia-line, #3f3f46);
        }
        .btn-action-back:hover { background: var(--ia-bg-raised, #3f3f46); color: var(--color-text-primary); }
        .btn-action-edit {
          background: var(--color-primary);
          color: var(--color-text-primary);
        }
        .btn-action-edit:hover { background: var(--color-primary); }
        .btn-action-inactivate {
          background: var(--status-warning-bg);
          color: var(--status-warning-text);
          border-color: rgba(234, 179, 8, 0.3);
        }
        .btn-action-inactivate:hover { background: rgba(234, 179, 8, 0.25); }
        .btn-action-reactivate {
          background: rgba(34, 197, 94, 0.15);
          color: var(--status-success-text);
          border-color: rgba(34, 197, 94, 0.3);
        }
        .btn-action-reactivate:hover { background: rgba(34, 197, 94, 0.25); }
        .btn-action-delete {
          background: rgba(239, 68, 68, 0.15);
          color: var(--status-danger-solid);
          border-color: rgba(239, 68, 68, 0.3);
        }
        .btn-action-delete:hover { background: rgba(239, 68, 68, 0.25); }

        /* Tabs Navigation */
        .eq-tabs-nav {
          display: flex;
          gap: 4px;
          border-bottom: 2px solid #27272a;
          margin-bottom: 24px;
          overflow-x: auto;
          scrollbar-width: thin;
        }
        .eq-tab-btn {
          background: transparent;
          border: none;
          padding: 12px 18px;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-text-disabled);
          cursor: pointer;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
          transition: all 0.2s;
        }
        .eq-tab-btn:hover { color: var(--color-text-primary); background: rgba(255, 255, 255, 0.03); }
        .eq-tab-btn.active {
          color: var(--ia-info-fg, #60a5fa);
          border-bottom-color: var(--color-ink);
          background: rgba(59, 130, 246, 0.05);
        }

        /* Tab Content Cards */
        .eq-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 20px;
        }
        .eq-info-panel {
          background: var(--ia-bg-panel, #18181b);
          border: 1px solid var(--ia-line, #27272a);
          border-radius: 8px;
          padding: 20px;
        }
        .eq-panel-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-border-default);
          margin: 0 0 16px 0;
          display: flex;
          align-items: center;
          gap: 8px;
          border-bottom: 1px solid #27272a;
          padding-bottom: 8px;
        }
        .eq-field-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px dashed #27272a;
          font-size: 0.875rem;
        }
        .eq-field-row:last-child { border-bottom: none; }
        .eq-field-label { color: var(--color-text-disabled); font-weight: 500; }
        .eq-field-val { color: var(--color-text-primary); font-weight: 600; text-align: right; }
        .eq-field-val.highlight { color: var(--ia-info-fg, #60a5fa); font-family: monospace; }
        .eq-field-val.badge-val { text-align: right; }

        .table-custom {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }
        .table-custom th {
          background: var(--ia-bg-panel, #121215);
          color: var(--color-text-disabled);
          padding: 10px 12px;
          text-align: left;
          font-weight: 600;
          border-bottom: 1px solid var(--ia-line, #27272a);
        }
        .table-custom td {
          padding: 10px 12px;
          border-bottom: 1px solid #27272a;
          color: var(--color-text-primary);
        }
        .table-custom tr:hover td { background: var(--ia-bg-panel, #202024); }
      </style>

      <div class="eq-detail-container">
        <!-- HIERARQUIA ORGANIZACIONAL BREADCRUMB -->
        <div style="background: rgba(37, 99, 235, 0.08); border: 1px solid rgba(59, 130, 246, 0.25); border-radius: 8px; padding: 10px 16px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;">
          <div style="display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: var(--color-graphite-400); flex-wrap: wrap;">
            <span style="display: inline-flex; align-items: center; gap: 5px; color: var(--color-border-default); font-weight: 600;">
              <i data-lucide="building-2" style="width: 15px; height: 15px;"></i> ${clientName}
            </span>
            <span style="color: var(--color-text-secondary);">&rarr;</span>
            <span style="display: inline-flex; align-items: center; gap: 5px; color: var(--color-graphite-300); font-weight: 600;">
              <i data-lucide="folder-kanban" style="width: 15px; height: 15px;"></i> ${projectName}
            </span>
            <span style="color: var(--color-text-secondary);">&rarr;</span>
            <span style="display: inline-flex; align-items: center; gap: 5px; color: var(--color-graphite-300); font-weight: 600;">
              <i data-lucide="map-pin" style="width: 15px; height: 15px;"></i> ${unitAreaName}
            </span>
            <span style="color: var(--color-text-secondary);">&rarr;</span>
            <span style="display: inline-flex; align-items: center; gap: 5px; color: var(--ia-info-fg, #60a5fa); font-family: monospace; font-weight: 700;">
              <i data-lucide="boxes" style="width: 15px; height: 15px;"></i> ${eq.tag}
            </span>
          </div>
          <div style="font-size: 0.75rem; color: var(--ia-info-fg, #60a5fa); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; display: flex; align-items: center; gap: 4px;">
            <i data-lucide="git-branch" style="width: 13px; height: 13px;"></i> Hierarquia Cadastral
          </div>
        </div>

        <!-- Header -->
        <div class="eq-detail-header">
          <div>
            <div class="eq-title-tag">
              ${eq.tag}
              ${this.getStatusBadge(eq.status)}
            </div>
            <p class="eq-subtitle">
              <span>${this.getTypeLabel(eq.equipmentType)}</span>
              <span>•</span>
              <strong style="color: var(--color-text-primary);">${eq.name}</strong>
              ${eq.system ? `<span>• Sistema: <span style="color: var(--ia-info-fg, #60a5fa);">${eq.system}</span></span>` : ''}
            </p>
          </div>

          <div class="eq-action-bar">
            <button class="btn-action btn-action-back" id="btn-back-list">
              <i data-lucide="arrow-left" style="width: 16px; height: 16px;"></i> Voltar
            </button>
            ${canUpdate ? `
              <button class="btn-action btn-action-edit" id="btn-edit-detail">
                <i data-lucide="edit-3" style="width: 16px; height: 16px;"></i> Editar
              </button>
            ` : ''}
            ${eq.status === 'ACTIVE' && canInactivate ? `
              <button class="btn-action btn-action-inactivate" id="btn-inactivate-detail">
                <i data-lucide="power" style="width: 16px; height: 16px;"></i> Inativar
              </button>
            ` : ''}
            ${eq.status === 'INACTIVE' && canReactivate ? `
              <button class="btn-action btn-action-reactivate" id="btn-reactivate-detail">
                <i data-lucide="rotate-ccw" style="width: 16px; height: 16px;"></i> Reativar
              </button>
            ` : ''}
            ${canDelete ? `
              <button class="btn-action btn-action-delete" id="btn-delete-detail">
                <i data-lucide="trash-2" style="width: 16px; height: 16px;"></i> Excluir
              </button>
            ` : ''}
          </div>
        </div>

        <!-- 7 Tabs Navigation -->
        <div class="eq-tabs-nav">
          <button class="eq-tab-btn ${this.activeTab === 'IDENTIFICATION' ? 'active' : ''}" data-tab="IDENTIFICATION">
            <i data-lucide="info" style="width: 16px; height: 16px;"></i> 1. Identificação
          </button>
          <button class="eq-tab-btn ${this.activeTab === 'PROJECT' ? 'active' : ''}" data-tab="PROJECT">
            <i data-lucide="layers" style="width: 16px; height: 16px;"></i> 2. Projeto
          </button>
          <button class="eq-tab-btn ${this.activeTab === 'OPERATION' ? 'active' : ''}" data-tab="OPERATION">
            <i data-lucide="activity" style="width: 16px; height: 16px;"></i> 3. Operação
          </button>
          <button class="eq-tab-btn ${this.activeTab === 'INTEGRITY' ? 'active' : ''}" data-tab="INTEGRITY">
            <i data-lucide="shield-check" style="width: 16px; height: 16px;"></i> 4. Integridade / Corrosão
          </button>
          <button class="eq-tab-btn ${this.activeTab === 'INSPECTION' ? 'active' : ''}" data-tab="INSPECTION">
            <i data-lucide="file-check" style="width: 16px; height: 16px;"></i> 5. Inspeção em Serviço & NR-13
          </button>
          <button class="eq-tab-btn ${this.activeTab === 'DOCUMENTS' ? 'active' : ''}" data-tab="DOCUMENTS">
            <i data-lucide="file-text" style="width: 16px; height: 16px;"></i> 6. Documentos (${this.documents.length})
          </button>
          <button class="eq-tab-btn ${this.activeTab === 'HISTORY' ? 'active' : ''}" data-tab="HISTORY">
            <i data-lucide="history" style="width: 16px; height: 16px;"></i> 7. Histórico & Snapshots (${this.snapshots.length})
          </button>
        </div>

        <!-- Tab Body -->
        <div class="eq-tab-content">
          ${this.renderTabContent()}
        </div>

        <div id="detail-modal-container"></div>
      </div>
    `;

    createIcons({
      icons: {
        ArrowLeft, Edit3, Power, Trash2, RotateCcw, ShieldCheck, FileText, Layers,
        Activity, Flame, Sparkles, CheckCircle, AlertTriangle, History, FileCheck,
        Info, Database, ExternalLink, Plus, GitBranch, Boxes, Building2, FolderKanban, MapPin
      },
      nameAttr: "data-lucide",
      root: this
    });

    this.bindEvents();
  }

  private renderTabContent(): string {
    const eq = this.equipment!;
    const isPiping = eq.equipmentType === 'PIPING_LINE';

    const client = this.clients.find(c => c.id === eq.clientId);
    const clientName = client ? (client.name || client.legalName) : (eq.clientId || 'Cliente não identificado');
    const project = this.projects.find(p => p.id === eq.projectId) || this.projects.find(p => p.clientId === eq.clientId);
    const projectName = project ? `${project.code} - ${project.name}` : (eq.projectId || 'Projeto não vinculado');
    const unitArea = this.unitAreas.find(u => u.id === (eq.unitAreaId || eq.areaId));
    const unitAreaName = unitArea ? `${unitArea.code} - ${unitArea.name}` : ((eq.unitAreaId || eq.areaId) || 'Unidade/Área não vinculada');

    switch (this.activeTab) {
      case 'IDENTIFICATION':
        return `
          <div class="eq-card-grid">
            <!-- HIERARQUIA ORGANIZACIONAL -->
            <div class="eq-info-panel" style="border-color: rgba(59, 130, 246, 0.4); background: rgba(59, 130, 246, 0.04);">
              <div class="eq-panel-title" style="color: var(--ia-info-fg, #60a5fa);">
                <i data-lucide="git-branch" style="width: 18px; height: 18px;"></i> Hierarquia Organizacional
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">1. Cliente</span>
                <span class="eq-field-val highlight" style="color: var(--color-border-default);"><i data-lucide="building-2" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${clientName}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">2. Projeto Vinculado</span>
                <span class="eq-field-val highlight" style="color: var(--ia-info-fg, #60a5fa);"><i data-lucide="folder" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${projectName}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">3. Unidade / área</span>
                <span class="eq-field-val highlight" style="color: var(--color-text-muted)"><i data-lucide="folder" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${unitAreaName}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Localização no Projeto</span>
                <span class="eq-field-val">${eq.location || '-'}</span>
              </div>
            </div>

            <div class="eq-info-panel">
              <div class="eq-panel-title">
                <i data-lucide="info" style="width: 18px; height: 18px;"></i> Identificação Geral
              </div>
              <div class="eq-field-row"><span class="eq-field-label">TAG</span><span class="eq-field-val highlight">${eq.tag}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Nome / Descrição</span><span class="eq-field-val">${eq.name}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Tipo de Equipamento</span><span class="eq-field-val">${this.getTypeLabel(eq.equipmentType)}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Sistema Pai</span><span class="eq-field-val">${eq.system || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Subsistema</span><span class="eq-field-val">${eq.subsystem || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Serviço</span><span class="eq-field-val">${eq.service || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Criticidade</span><span class="eq-field-val">${eq.criticality || 'Média'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Status</span><span class="eq-field-val badge-val">${this.getStatusBadge(eq.status)}</span></div>
            </div>

            <div class="eq-info-panel">
              <div class="eq-panel-title">
                <i data-lucide="database" style="width: 18px; height: 18px;"></i> Dados de Fabricação
              </div>
              <div class="eq-field-row"><span class="eq-field-label">Fabricante</span><span class="eq-field-val">${eq.manufacturer || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Modelo</span><span class="eq-field-val">${eq.model || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Número de Série</span><span class="eq-field-val">${eq.serialNumber || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Ano de Fabricação</span><span class="eq-field-val">${eq.yearOfManufacture || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Data de Instalação</span><span class="eq-field-val">${eq.installationDate || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Data de Comissionamento</span><span class="eq-field-val">${eq.commissioningDate || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Versão Cadastral</span><span class="eq-field-val">v${eq.version || 1}</span></div>
            </div>

            ${isPiping ? `
              <div class="eq-info-panel" style="grid-column: 1 / -1; border-color: rgba(59, 130, 246, 0.4); background: rgba(59, 130, 246, 0.03);">
                <div class="eq-panel-title" style="color: var(--ia-info-fg, #60a5fa);">
                  <i data-lucide="layers" style="width: 18px; height: 18px;"></i> Especificidades de Tubulação (Piping Line)
                </div>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px;">
                  <div>
                    <div class="eq-field-row"><span class="eq-field-label">Número da Linha</span><span class="eq-field-val highlight">${eq.lineNumber || eq.tag}</span></div>
                    <div class="eq-field-row"><span class="eq-field-label">Fluido</span><span class="eq-field-val">${eq.fluid || '-'}</span></div>
                    <div class="eq-field-row"><span class="eq-field-label">Serviço</span><span class="eq-field-val">${eq.service || '-'}</span></div>
                    <div class="eq-field-row"><span class="eq-field-label">Classe do Fluido (NR-13)</span><span class="eq-field-val highlight">${eq.fluidClass || '-'}</span></div>
                  </div>
                  <div>
                    <div class="eq-field-row">
                      <span class="eq-field-label">DE (Origem)</span>
                      <span class="eq-field-val" style="color: var(--color-border-default);">
                        ${this.renderLinkedRef(eq.fromEquipmentId, eq.fromReference)}
                      </span>
                    </div>
                    <div class="eq-field-row">
                      <span class="eq-field-label">PARA (Destino)</span>
                      <span class="eq-field-val" style="color: var(--color-border-default);">
                        ${this.renderLinkedRef(eq.toEquipmentId, eq.toReference)}
                      </span>
                    </div>
                    <div class="eq-field-row"><span class="eq-field-label">Sistema Pai Obrigatório</span><span class="eq-field-val">${eq.system || '-'}</span></div>
                  </div>
                </div>
              </div>
            ` : ''}
          </div>
        `;

      case 'PROJECT':
        return `
          <div class="eq-card-grid">
            <div class="eq-info-panel">
              <div class="eq-panel-title">
                <i data-lucide="layers" style="width: 18px; height: 18px;"></i> Condições de Projeto (Design)
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Pressão de Projeto</span>
                <span class="eq-field-val highlight">${this.formatMeasurement(eq.designPressure)}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Temperatura de Projeto</span>
                <span class="eq-field-val highlight">${this.formatMeasurement(eq.designTemperature)}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Material de Fabricação</span>
                <span class="eq-field-val">${eq.material || '-'}</span>
              </div>
              <div style="display: flex; gap: 12px;">
                <div class="eq-field-row" style="flex: 1;">
                  <span class="eq-field-label">CÓDIGO DE PROJETO</span>
                  <span class="eq-field-val highlight">${eq.projectCode || '-'}</span>
                </div>
                <div class="eq-field-row" style="flex: 1;">
                  <span class="eq-field-label">NORMA DE FABRICAÇÃO</span>
                  <span class="eq-field-val highlight">${eq.fabricationStandard || '-'}</span>
                </div>
              </div>
              <div style="display: flex; gap: 12px;">
                <div class="eq-field-row" style="flex: 1;">
                  <span class="eq-field-label">Desenho de Referência</span>
                  <span class="eq-field-val highlight">${eq.drawingReference || eq.projectDrawingFlowchart || '-'}</span>
                </div>
                <div class="eq-field-row" style="flex: 1;">
                  <span class="eq-field-label">Fluxograma (P&ID/PFD)</span>
                  <span class="eq-field-val highlight">${eq.flowDiagramReference || eq.projectDrawingFlowchart || '-'}</span>
                </div>
              </div>
            </div>
            ${this.buildSystemConsolidatedReferencesHtml(eq)}

            <div class="eq-info-panel">
              <div class="eq-panel-title">
                <i data-lucide="shield-check" style="width: 18px; height: 18px;"></i> Espessuras de Projeto
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Espessura Nominal (mm)</span>
                <span class="eq-field-val highlight">${this.formatMeasurement(eq.nominalThickness)}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Sobre-espessura de Corrosão (mm)</span>
                <span class="eq-field-val highlight" style="color: var(--status-warning-text);">${this.formatMeasurement(eq.corrosionAllowance)}</span>
              </div>
              ${isPiping ? `
                <div class="eq-field-row">
                  <span class="eq-field-label">SPEC</span>
                  <span class="eq-field-val">${eq.spec || '-'}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Classe de Pressão</span>
                  <span class="eq-field-val">${eq.pressureClass ? `${eq.pressureClass}#` : '-'}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">NORMA DIMENSIONAL</span>
                  <span class="eq-field-val">${eq.dimensionalStandard || '-'}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">FAMÍLIA DE MATERIAL</span>
                  <span class="eq-field-val">${eq.materialFamily || '-'}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">ESPECIFICAÇÃO</span>
                  <span class="eq-field-val">${eq.materialSpecification || '-'}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">GRAU</span>
                  <span class="eq-field-val">${eq.materialGrade || '-'}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">NPS</span>
                  <span class="eq-field-val">${eq.nps || '-'}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">SCHEDULE</span>
                  <span class="eq-field-val">${eq.schedule ? `Sch ${eq.schedule}` : ''}${eq.schedule && eq.pipingDesignation ? ' (' : ''}${eq.pipingDesignation || ''}${eq.schedule && eq.pipingDesignation ? ')' : ''}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Diâmetro Externo (mm)</span>
                  <span class="eq-field-val">${this.formatMeasurement(eq.outsideDiameter)}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Diâmetro Interno (mm)</span>
                  <span class="eq-field-val">${this.formatMeasurement(eq.calculatedInnerDiameter)}</span>
                </div>
                ${eq.dimensionSource === 'MANUAL' ? `
                <div class="eq-field-row">
                  <span class="eq-field-label">Fonte (Inclusão Manual)</span>
                  <span class="eq-field-val" style="color: var(--status-warning-text);">${eq.dimensionManualSource || '-'}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Justificativa Manual</span>
                  <span class="eq-field-val" style="color: var(--status-warning-text);">${eq.dimensionManualJustification || '-'}</span>
                </div>
                ` : `
                <div class="eq-field-row">
                  <span class="eq-field-label">Fonte Dimensional</span>
                  <span class="eq-field-val" style="color: var(--status-success-text);">Catálogo Automático</span>
                </div>
                `}
                <div class="eq-field-row">
                  <span class="eq-field-label">Isolamento</span>
                  <span class="eq-field-val">${eq.insulation || '-'}</span>
                </div>
              ` : ''}
            </div>
          </div>
        `;

      case 'OPERATION':
        return `
          <div class="eq-card-grid">
            <div class="eq-info-panel">
              <div class="eq-panel-title">
                <i data-lucide="activity" style="width: 18px; height: 18px;"></i> Condições Operacionais
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Pressão de Operação</span>
                <span class="eq-field-val highlight">${this.formatMeasurement(eq.operatingPressure)}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Temperatura de Operação</span>
                <span class="eq-field-val highlight">${this.formatMeasurement(eq.operatingTemperature)}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Fluido Operacional</span>
                <span class="eq-field-val">${eq.fluid || eq.service || '-'}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Código do Fluido</span>
                <span class="eq-field-val">${eq.fluidCode || '-'}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Serviço / Função</span>
                <span class="eq-field-val">${eq.service || '-'}</span>
              </div>
              <div class="eq-field-row">
                <span class="eq-field-label">Condição Operacional</span>
                <span class="eq-field-val">${eq.operatingCondition || 'Regime Contínuo'}</span>
              </div>
            </div>

            <div class="eq-info-panel" style="background: rgba(39, 39, 42, 0.4);">
              <div class="eq-panel-title">
                <i data-lucide="info" style="width: 18px; height: 18px;"></i> Comparativo: Projeto vs Operação
              </div>
              <div style="font-size: 0.85rem; color: var(--color-text-disabled); line-height: 1.6;">
                <p style="margin: 0 0 10px 0;">
                  As condições de <strong>Projeto</strong> definem os limites seguros construtivos estruturais, enquanto as condições de <strong>Operação</strong> retratam a rotina de processo.
                </p>
                <div style="background: var(--color-input-bg); border: 1px solid #27272a; border-radius: 6px; padding: 12px; margin-top: 12px;">
                  <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
                    <span>Pressão Projeto / Operação:</span>
                    <strong style="color: var(--color-text-primary);">${this.formatMeasurement(eq.designPressure)} / ${this.formatMeasurement(eq.operatingPressure)}</strong>
                  </div>
                  <div style="display: flex; justify-content: space-between;">
                    <span>Temperatura Projeto / Operação:</span>
                    <strong style="color: var(--color-text-primary);">${this.formatMeasurement(eq.designTemperature)} / ${this.formatMeasurement(eq.operatingTemperature)}</strong>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-top: 6px;">
                    <span>PMTA:</span>
                    <strong style="color: var(--color-text-primary);">${eq.pmtaValue ? eq.pmtaValue + ' ' + (eq.pmtaUnit || 'kgf/cm²') : '-'}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;

      case 'INTEGRITY': {
        const mechanisms = eq.damageMechanisms || [];
        return `
          <div style="display: flex; flex-direction: column; gap: 20px;">
            <div class="eq-card-grid">
              <div class="eq-info-panel">
                <div class="eq-panel-title">
                  <i data-lucide="shield-check" style="width: 18px; height: 18px;"></i> Parâmetros de Espessura e Corrosão
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Espessura Nominal</span>
                  <span class="eq-field-val highlight">${this.formatMeasurement(eq.nominalThickness)}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Sobre-espessura para Corrosão</span>
                  <span class="eq-field-val highlight" style="color: var(--status-warning-text);">${this.formatMeasurement(eq.corrosionAllowance)}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Espessura Mínima Requerida</span>
                  <span class="eq-field-val highlight" style="color: var(--status-danger-solid);">
                    ${eq.minimumRequiredThickness ? `${eq.minimumRequiredThickness.value} ${eq.minimumRequiredThickness.unit}` : '-'}
                  </span>
                </div>
                ${eq.minimumRequiredThickness ? `
                  <div style="font-size: 0.75rem; color: var(--color-text-disabled); background: var(--color-input-bg); padding: 8px 12px; border-radius: 4px; margin-top: 8px;">
                    <div>Origem: <strong>${eq.minimumRequiredThickness.source}</strong></div>
                    ${eq.minimumRequiredThickness.calculationBasis ? `<div>Base: ${eq.minimumRequiredThickness.calculationBasis}</div>` : ''}
                    ${eq.minimumRequiredThickness.responsible ? `<div>Responsável: ${eq.minimumRequiredThickness.responsible}</div>` : ''}
                    <div>Data: ${new Date(eq.minimumRequiredThickness.date || '').toLocaleDateString('pt-BR')}</div>
                  </div>
                ` : ''}
              </div>

              <div class="eq-info-panel" style="background: rgba(59, 130, 246, 0.05); border-color: rgba(59, 130, 246, 0.3);">
                <div class="eq-panel-title" style="color: var(--ia-info-fg, #60a5fa);">
                  <i data-lucide="sparkles" style="width: 18px; height: 18px;"></i> INSPECT IA REVIEW — Mecanismos de Dano
                </div>
                <p style="font-size: 0.85rem; color: var(--color-text-primary); line-height: 1.5; margin: 0 0 16px 0;">
                  O INSPECT IA REVIEW aplica <strong>regras fixas</strong> sobre o tipo de ativo e o isolamento para <strong>sugerir</strong> mecanismos de dano a avaliar (referência: API 571). Não é diagnóstico.
                  <br><br>
                  <em style="color: var(--status-warning-text);">Regra de Segurança:</em> Mecanismos sugeridos entram com status <strong>SUSPEITO</strong> e jamais são confirmados sem a ação direta do profissional responsável.
                </p>
                <button class="btn-action" id="btn-ai-suggest-dm" style="background: var(--color-primary); color: var(--color-text-primary);">
                  <i data-lucide="sparkles" style="width: 16px; height: 16px;"></i> Sugerir Mecanismos (regras)
                </button>
              </div>
            </div>

            <div class="eq-info-panel">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; border-bottom: 1px solid #27272a; padding-bottom: 8px;">
                <div class="eq-panel-title" style="margin: 0; border: none; padding: 0;">
                  <i data-lucide="flame" style="width: 18px; height: 18px; color: var(--ia-warning-fg, #f97316);"></i> Mecanismos de Dano Ativos / Suspeitos (${mechanisms.length})
                </div>
                <button class="btn-action" id="btn-add-manual-dm" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border-color: var(--ia-line, #3f3f46);">
                  <i data-lucide="plus" style="width: 14px; height: 14px;"></i> Adicionar Manual
                </button>
              </div>

              ${mechanisms.length === 0 ? `
                <div style="padding: 30px; text-align: center; color: var(--color-text-primary); border: 1px dashed #27272a; border-radius: 6px;">
                  Nenhum mecanismo de dano vinculado a este ativo técnico.
                </div>
              ` : `
                <div style="overflow-x: auto;">
                  <table class="table-custom">
                    <thead>
                      <tr>
                        <th>Mecanismo de Dano</th>
                        <th>Código / Ref.</th>
                        <th>Status</th>
                        <th>Origem</th>
                        <th>Identificado em</th>
                        <th>Responsável</th>
                        <th>Notas</th>
                        <th style="text-align: right;">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${mechanisms.map((m, idx) => `
                        <tr>
                          <td><strong style="color: var(--color-text-primary);">${m.name}</strong></td>
                          <td><code style="color: var(--color-border-default); background: var(--color-input-bg); padding: 2px 6px; border-radius: 4px;">${m.damageMechanismId}</code></td>
                          <td>
                            ${m.status === 'CONFIRMED' 
                              ? `<span style="background: rgba(34, 197, 94, 0.15); color: var(--status-success-text); padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 0.75rem;">CONFIRMADO</span>`
                              : `<span style="background: var(--status-warning-bg); color: var(--status-warning-text); padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 0.75rem;">SUSPEITO</span>`}
                          </td>
                          <td><span style="font-size: 0.75rem; color: var(--color-text-disabled);">${m.source}</span></td>
                          <td>${new Date(m.identifiedAt).toLocaleDateString('pt-BR')}</td>
                          <td>${m.identifiedBy || '-'}</td>
                          <td style="max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${m.notes || '-'}</td>
                          <td style="text-align: right;">
                            ${m.status === 'SUSPECTED' ? `
                              <button class="btn-action btn-confirm-dm" data-index="${idx}" style="padding: 4px 8px; font-size: 0.75rem; background: var(--status-success-text); color: var(--color-text-primary);">
                                Confirmar
                              </button>
                            ` : ''}
                            <button class="btn-action btn-remove-dm" data-index="${idx}" style="padding: 4px 8px; font-size: 0.75rem; background: transparent; color: var(--status-danger-solid);">
                              ✖
                            </button>
                          </td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                </div>
              `}
            </div>
          </div>
        `;
      }

      case 'INSPECTION':
        return `
          <div style="display: flex; flex-direction: column; gap: 20px;">
            <div class="eq-card-grid">
              <div class="eq-info-panel">
                <div class="eq-panel-title">
                  <i data-lucide="file-check" style="width: 18px; height: 18px;"></i> Norma de Inspeção em Serviço
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Norma de Inspeção em Serviço</span>
                  <span class="eq-field-val highlight" style="color: var(--ia-info-fg, #60a5fa);">${eq.inServiceInspectionCode || 'Não informada'}</span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Norma de Fabricação</span>
                  <span class="eq-field-val">${eq.fabricationStandard || '-'}</span>
                </div>
              </div>

              <div class="eq-info-panel" style="border-color: rgba(245, 158, 11, 0.4); background: rgba(245, 158, 11, 0.03);">
                <div class="eq-panel-title" style="color: var(--status-warning-text);">
                  <i data-lucide="shield-check" style="width: 18px; height: 18px;"></i> Enquadramento NR-13
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Aplicabilidade NR-13</span>
                  <span class="eq-field-val">
                    ${eq.nr13Applicability === 'APPLICABLE' 
                      ? '<span style="color: var(--status-success-text); font-weight: 700;">APLICÁVEL</span>' 
                      : (eq.nr13Applicability === 'NOT_APPLICABLE' 
                        ? '<span style="color: var(--color-text-disabled);">NÃO APLICÁVEL</span>' 
                        : '<span style="color: var(--status-warning-text);">NÃO CLASSIFICADO</span>')}
                  </span>
                </div>
                <div class="eq-field-row">
                  <span class="eq-field-label">Categoria NR-13</span>
                  <span class="eq-field-val highlight">
                    ${eq.nr13Applicability === 'APPLICABLE' ? (eq.nr13Category || 'Pendente de Classificação') : 'Dispensada'}
                  </span>
                </div>
              </div>
            </div>

            <div class="eq-info-panel">
              <div class="eq-panel-title">
                <i data-lucide="history" style="width: 18px; height: 18px;"></i> Inspeções Registradas (${this.inspections.length})
              </div>
              ${this.inspections.length === 0 ? `
                <div style="padding: 24px; text-align: center; color: var(--color-text-primary); border: 1px dashed #27272a; border-radius: 6px;">
                  Nenhuma inspeção registrada para este equipamento até o momento.
                </div>
              ` : `
                <div style="overflow-x: auto;">
                  <table class="table-custom">
                    <thead>
                      <tr>
                        <th>ID da Inspeção</th>
                        <th>Natureza</th>
                        <th>Tipo</th>
                        <th>Modalidade</th>
                        <th>Status</th>
                        <th>Data de Início</th>
                        <th style="text-align: right;">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${this.inspections.map(i => `
                        <tr>
                          <td><code style="color: var(--ia-info-fg, #60a5fa);">${i.id}</code></td>
                          <td><strong>${i.nature}</strong></td>
                          <td>${i.inspectionType}</td>
                          <td>${i.modality}</td>
                          <td><span style="background: var(--ia-bg-panel, #27272a); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem;">${inspectionStatusLabel(i.status)}</span></td>
                          <td>${new Date(i.startDate).toLocaleDateString('pt-BR')}</td>
                          <td style="text-align: right;">
                            <a href="#/inspections/checklist/${i.id}" style="color: var(--ia-info-fg, #60a5fa); text-decoration: none; font-size: 0.8rem; display: inline-flex; align-items: center; gap: 4px;">
                              Ver Checklist <i data-lucide="external-link" style="width: 12px; height: 12px;"></i>
                            </a>
                          </td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                </div>
              `}
            </div>
          </div>
        `;

      case 'DOCUMENTS':
        return `
          <div class="eq-info-panel">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; border-bottom: 1px solid #27272a; padding-bottom: 8px;">
              <div class="eq-panel-title" style="margin: 0; border: none; padding: 0;">
                <i data-lucide="file-text" style="width: 18px; height: 18px;"></i> Documentação Técnica do Ativo
              </div>
              <button class="btn-action" id="btn-link-doc" style="background: var(--color-primary); color: var(--color-text-primary);">
                <i data-lucide="plus" style="width: 14px; height: 14px;"></i> Vincular Documento
              </button>
            </div>

            ${this.documents.length === 0 ? `
              <div style="padding: 30px; text-align: center; color: var(--color-text-primary); border: 1px dashed #27272a; border-radius: 6px;">
                Nenhum documento técnico vinculado a este equipamento.
              </div>
            ` : `
              <div style="overflow-x: auto;">
                <table class="table-custom">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Tipo de Documento</th>
                      <th>Nome do Arquivo</th>
                      <th>Data de Upload</th>
                      <th>Notas</th>
                      <th style="text-align: right;">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${this.documents.map(d => `
                      <tr>
                        <td><strong style="color: var(--color-text-primary);">${d.title}</strong></td>
                        <td><span style="background: var(--ia-bg-panel, #27272a); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem;">${d.documentType}</span></td>
                        <td><code style="color: var(--color-text-disabled);">${d.filename}</code></td>
                        <td>${new Date(d.uploadedAt).toLocaleDateString('pt-BR')}</td>
                        <td>${d.notes || '-'}</td>
                        <td style="text-align: right;">
                          <button class="btn-action btn-unlink-doc" data-id="${d.id}" style="padding: 4px 8px; font-size: 0.75rem; background: transparent; color: var(--status-danger-solid);">
                            Desvincular
                          </button>
                        </td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            `}
          </div>
        `;

      case 'HISTORY':
        return `
          <div style="display: flex; flex-direction: column; gap: 20px;">
            <div class="eq-info-panel">
              <div class="eq-panel-title">
                <i data-lucide="history" style="width: 18px; height: 18px;"></i> Snapshots Imutáveis de Inspeção (${this.snapshots.length})
              </div>
              <p style="font-size: 0.85rem; color: var(--color-text-disabled); margin: 0 0 16px 0;">
                Cada inspeção gera um <strong>EquipmentSnapshot</strong> imutável que congela pressões, espessuras, DE/PARA e normas no momento da vistoria. Edições futuras no equipamento não alteram relatórios passados.
              </p>

              ${this.snapshots.length === 0 ? `
                <div style="padding: 30px; text-align: center; color: var(--color-text-primary); border: 1px dashed #27272a; border-radius: 6px;">
                  Nenhum snapshot histórico registrado para este equipamento.
                </div>
              ` : `
                <div style="overflow-x: auto;">
                  <table class="table-custom">
                    <thead>
                      <tr>
                        <th>ID do Snapshot</th>
                        <th>Versão Capturada</th>
                        <th>Data / Hora da Captura</th>
                        <th>Motivo / Evento</th>
                        <th style="text-align: right;">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${this.snapshots.map(s => `
                        <tr>
                          <td><code style="color: var(--ia-info-fg, #60a5fa); font-weight: 600;">${s.id}</code></td>
                          <td>v${s.equipmentVersion}</td>
                          <td>${new Date(s.capturedAt).toLocaleString('pt-BR')}</td>
                          <td>${s.reason}</td>
                          <td style="text-align: right;">
                            <button class="btn-action btn-view-snapshot" data-id="${s.id}" style="padding: 4px 10px; font-size: 0.75rem; background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary);">
                              Ver Dados do Snapshot
                            </button>
                          </td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                </div>
              `}
            </div>

            <div class="eq-info-panel">
              <div class="eq-panel-title">
                <i data-lucide="database" style="width: 18px; height: 18px;"></i> Trilha de Auditoria Cadastral
              </div>
              <div class="eq-field-row"><span class="eq-field-label">Criado em:</span><span class="eq-field-val">${new Date(eq.createdAt).toLocaleString('pt-BR')}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Última Atualização:</span><span class="eq-field-val">${new Date(eq.updatedAt).toLocaleString('pt-BR')}</span></div>
              ${eq.inactivatedAt ? `
                <div class="eq-field-row" style="color: var(--status-danger-solid);">
                  <span class="eq-field-label">Inativado em:</span>
                  <span class="eq-field-val">${new Date(eq.inactivatedAt).toLocaleString('pt-BR')} por ${eq.inactivatedBy || 'Usuário'}</span>
                </div>
                <div class="eq-field-row" style="color: var(--status-danger-solid);">
                  <span class="eq-field-label">Motivo da Inativação:</span>
                  <span class="eq-field-val">${eq.inactivationReason || '-'}</span>
                </div>
              ` : ''}
            </div>
          </div>
        `;

      default:
        return '';
    }
  }

  private renderLinkedRef(id?: string | null, text?: string | null): string {
    if (id) {
      const target = this.relatedEquipments.find(e => e.id === id);
      if (target) {
        return `<a href="#/equipment/${target.id}" style="color: var(--ia-info-fg, #60a5fa); text-decoration: none; font-weight: 600;">${target.tag} (${target.name})</a>`;
      }
    }
    return text || '-';
  }

  private bindEvents() {
    this.querySelector('#btn-back-list')?.addEventListener('click', () => {
      router.navigate('/equipment');
    });

    this.querySelectorAll('.eq-tab-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const tab = (e.currentTarget as HTMLElement).getAttribute('data-tab') as any;
        if (tab) {
          this.activeTab = tab;
          this.render();
        }
      });
    });

    // Action buttons
    this.querySelector('#btn-edit-detail')?.addEventListener('click', () => {
      this.openEditModal();
    });

    this.querySelector('#btn-inactivate-detail')?.addEventListener('click', () => {
      this.openInactivateModal();
    });

    this.querySelector('#btn-reactivate-detail')?.addEventListener('click', async () => {
      if (confirm(`Deseja reativar o equipamento ${this.equipment?.tag}?`)) {
        try {
          const ctx = sessionRequestContext();
          await window.app.platform.equipmentService.reactivateEquipment(ctx, this.equipment!.id, "Reativação na tela de detalhes");
          this.loadEquipmentData();
        } catch (err: any) {
          alert(`Erro ao reativar: ${err.message}`);
        }
      }
    });

    this.querySelector('#btn-delete-detail')?.addEventListener('click', () => {
      this.openDeleteModal();
    });

    // Damage Mechanisms handlers
    this.querySelector('#btn-ai-suggest-dm')?.addEventListener('click', () => {
      this.suggestDamageMechanismsByRule();
    });

    this.querySelector('#btn-add-manual-dm')?.addEventListener('click', () => {
      this.openAddDamageMechanismModal();
    });

    this.querySelectorAll('.btn-confirm-dm').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const idx = Number((e.currentTarget as HTMLElement).getAttribute('data-index'));
        this.confirmDamageMechanism(idx);
      });
    });

    this.querySelectorAll('.btn-remove-dm').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const idx = Number((e.currentTarget as HTMLElement).getAttribute('data-index'));
        this.removeDamageMechanism(idx);
      });
    });

    // Document handlers
    this.querySelector('#btn-link-doc')?.addEventListener('click', () => {
      this.openLinkDocModal();
    });

    this.querySelectorAll('.btn-unlink-doc').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const docId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (docId && confirm("Deseja desvincular este documento?")) {
          try {
            const ctx = sessionRequestContext();
            await window.app.platform.equipmentService.unlinkDocument(ctx, this.equipment!.id, docId);
            this.loadEquipmentData();
          } catch (err: any) {
            alert(`Erro ao desvincular documento: ${err.message}`);
          }
        }
      });
    });

    // View Snapshot modal
    this.querySelectorAll('.btn-view-snapshot').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const snapId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const snap = this.snapshots.find(s => s.id === snapId);
        if (snap) this.openSnapshotModal(snap);
      });
    });
  }

  // SUGESTÃO POR REGRA (INSPECT IA REVIEW): entra como SUSPEITO — a pessoa confirma ou descarta.
  private async suggestDamageMechanismsByRule() {
    if (!this.equipment) return;
    const isPiping = this.equipment.equipmentType === 'PIPING_LINE';
    const suggested: EquipmentDamageMechanism[] = [];

    // Realistic API 571 suggestion heuristics based on technical asset parameters
    if (isPiping || this.equipment.insulation) {
      suggested.push({
        id: `DM-${Date.now()}-1`,
        equipmentId: this.equipment.id,
        damageMechanismId: 'DM-CUI',
        name: 'Corrosão Sob Isolamento (CUI)',
        status: 'SUSPECTED',
        source: 'AI_REVIEW_SUGGESTION',
        identifiedAt: new Date().toISOString(),
        identifiedBy: 'INSPECT IA REVIEW (regra — referência API 571)',
        notes: 'Sugerido devido à presença de isolamento térmico e faixa de temperatura suscetível.'
      });
    }

    suggested.push({
      id: `DM-${Date.now()}-2`,
      equipmentId: this.equipment.id,
      damageMechanismId: 'DM-ATM-CORR',
      name: 'Corrosão Atmosférica',
      status: 'SUSPECTED',
      source: 'AI_REVIEW_SUGGESTION',
      identifiedAt: new Date().toISOString(),
      identifiedBy: 'INSPECT IA REVIEW (regra — referência API 571)',
      notes: 'Sugerido para ativos industriais expostos à atmosfera e umidade.'
    });

    if (isPiping) {
      suggested.push({
        id: `DM-${Date.now()}-3`,
        equipmentId: this.equipment.id,
        damageMechanismId: 'DM-EROSION',
        name: 'Erosão / Erosão-Corrosão',
        status: 'SUSPECTED',
        source: 'AI_REVIEW_SUGGESTION',
        identifiedAt: new Date().toISOString(),
        identifiedBy: 'INSPECT IA REVIEW (regra — referência API 571)',
        notes: 'Sugerido para linhas de transporte com possíveis turbulências ou velocidades elevadas.'
      });
    }

    // Merge without duplicating existing
    const existing = this.equipment.damageMechanisms || [];
    const newOnes = suggested.filter(s => !existing.some(e => e.damageMechanismId === s.damageMechanismId));

    if (newOnes.length === 0) {
      alert("Os mecanismos sugeridos pelas regras para este ativo já estão cadastrados.");
      return;
    }

    const updatedMechanisms = [...existing, ...newOnes];
    try {
      const ctx = sessionRequestContext();
      await window.app.platform.equipmentService.updateEquipment(ctx, {
        ...this.equipment,
        damageMechanisms: updatedMechanisms
      });
      alert(`O INSPECT IA REVIEW sugeriu ${newOnes.length} mecanismo(s) de dano por regra. Eles foram registrados como "SUSPEITO" e aguardam sua confirmação técnica.`);
      this.loadEquipmentData();
    } catch (err: any) {
      alert(`Erro ao registrar sugestões: ${err.message}`);
    }
  }

  private async confirmDamageMechanism(index: number) {
    if (!this.equipment) return;
    const mechanisms = [...(this.equipment.damageMechanisms || [])];
    if (mechanisms[index]) {
      mechanisms[index] = {
        ...mechanisms[index],
        status: 'CONFIRMED',
        identifiedBy: contextStore.get().userName || 'Profissional Técnico Habilitado',
        notes: (mechanisms[index].notes ? mechanisms[index].notes + ' ' : '') + `[Confirmado em ${new Date().toLocaleDateString('pt-BR')}]`
      };

      try {
        const ctx = sessionRequestContext();
        await window.app.platform.equipmentService.updateEquipment(ctx, {
          ...this.equipment,
          damageMechanisms: mechanisms
        });
        this.loadEquipmentData();
      } catch (err: any) {
        alert(`Erro ao confirmar mecanismo: ${err.message}`);
      }
    }
  }

  private async removeDamageMechanism(index: number) {
    if (!this.equipment) return;
    if (confirm("Deseja remover este mecanismo de dano deste equipamento?")) {
      const mechanisms = (this.equipment.damageMechanisms || []).filter((_, idx) => idx !== index);
      try {
        const ctx = sessionRequestContext();
        await window.app.platform.equipmentService.updateEquipment(ctx, {
          ...this.equipment,
          damageMechanisms: mechanisms
        });
        this.loadEquipmentData();
      } catch (err: any) {
        alert(`Erro ao remover mecanismo: ${err.message}`);
      }
    }
  }

  private openAddDamageMechanismModal() {
    const modalContainer = this.querySelector('#detail-modal-container') as HTMLElement;
    if (!modalContainer || !this.equipment) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 500px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="flame" style="color: var(--ia-warning-fg, #f97316); width: 20px; height: 20px;"></i> Adicionar Mecanismo de Dano
            </h3>
            <button id="btn-close-dm-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✖</button>
          </div>

          <form id="form-add-dm" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Catálogo de Mecanismos (API 571)</label>
              <select id="select-standard-dm" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                <option value="">Selecione do catálogo padrão...</option>
                ${STANDARD_DAMAGE_MECHANISMS.map(s => `<option value="${s.id}" data-name="${s.name}">${s.name} (${s.code})</option>`).join('')}
              </select>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nome do Mecanismo *</label>
              <input type="text" name="name" id="dm-input-name" required placeholder="Ex: Corrosão por Pites" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Código de Referência</label>
              <input type="text" name="code" id="dm-input-code" placeholder="Ex: API-571-3.22" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Status Inicial</label>
              <select name="status" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                <option value="CONFIRMED">Confirmado pelo Técnico</option>
                <option value="SUSPECTED">Suspeito (Em Análise)</option>
              </select>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Observações / Localização do Dano</label>
              <textarea name="notes" rows="2" placeholder="Notas sobre extensão, localização ou histórico..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-dm" class="btn-action btn-action-back">Cancelar</button>
              <button type="submit" class="btn-action" style="background: var(--color-primary); color: var(--color-text-primary);">Adicionar Mecanismo</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({ icons: { Flame }, nameAttr: "data-lucide", root: modalContainer });

    const selectStd = modalContainer.querySelector('#select-standard-dm') as HTMLSelectElement;
    const inputName = modalContainer.querySelector('#dm-input-name') as HTMLInputElement;
    const inputCode = modalContainer.querySelector('#dm-input-code') as HTMLInputElement;

    selectStd?.addEventListener('change', () => {
      const selected = STANDARD_DAMAGE_MECHANISMS.find(s => s.id === selectStd.value);
      if (selected) {
        inputName.value = selected.name;
        inputCode.value = selected.code;
      }
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-dm-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-dm')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-add-dm') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const newDm: EquipmentDamageMechanism = {
        id: `DM-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        equipmentId: this.equipment!.id,
        damageMechanismId: (formData.get('code') as string) || (formData.get('name') as string),
        name: formData.get('name') as string,
        status: formData.get('status') as any,
        source: 'MANUAL',
        identifiedAt: new Date().toISOString(),
        identifiedBy: contextStore.get().userName || 'Profissional Técnico',
        notes: (formData.get('notes') as string) || null
      };

      const updatedMechanisms = [...(this.equipment!.damageMechanisms || []), newDm];
      try {
        const ctx = sessionRequestContext();
        await window.app.platform.equipmentService.updateEquipment(ctx, {
          ...this.equipment!,
          damageMechanisms: updatedMechanisms
        });
        closeModal();
        this.loadEquipmentData();
      } catch (err: any) {
        alert(`Erro ao adicionar mecanismo: ${err.message}`);
      }
    });
  }

  private openSnapshotModal(snap: EquipmentSnapshot) {
    const modalContainer = this.querySelector('#detail-modal-container') as HTMLElement;
    if (!modalContainer) return;

    let payloadObj: any = {};
    try {
      payloadObj = JSON.parse(snap.payloadJson);
    } catch {
      payloadObj = snap.equipmentData || {};
    }

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 700px; max-height: 85vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--ia-info-fg, #60a5fa); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="history" style="width: 20px; height: 20px;"></i> Registro Histórico Imutável (${snap.id})
            </h3>
            <button id="btn-close-snap-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✖</button>
          </div>

          <div style="padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px; font-size: 0.875rem;">
            <div style="background: rgba(59, 130, 246, 0.08); border: 1px solid rgba(59, 130, 246, 0.3); border-radius: 6px; padding: 12px; color: var(--color-border-default);">
              <strong>Snapshot Congelado:</strong> Capturado em ${new Date(snap.capturedAt).toLocaleString('pt-BR')} (Versão do Ativo: v${snap.equipmentVersion}).
              <br>Motivo: <em>${snap.reason}</em>
            </div>

            <div style="background: var(--color-input-bg); border: 1px solid #27272a; border-radius: 6px; padding: 16px;">
              <h4 style="margin: 0 0 10px 0; color: var(--color-text-primary);">Dados Congelados da Inspeção</h4>
              <div class="eq-field-row"><span class="eq-field-label">TAG / Nome:</span><span class="eq-field-val">${payloadObj.tag} - ${payloadObj.name}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Pressão de Projeto:</span><span class="eq-field-val">${this.formatMeasurement(payloadObj.designPressure)}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Pressão de Operação:</span><span class="eq-field-val">${this.formatMeasurement(payloadObj.operatingPressure)}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Temperatura Projeto / Operação:</span><span class="eq-field-val">${this.formatMeasurement(payloadObj.designTemperature)} / ${this.formatMeasurement(payloadObj.operatingTemperature)}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">PMTA:</span><span class="eq-field-val">${payloadObj.pmtaValue ? payloadObj.pmtaValue + ' ' + (payloadObj.pmtaUnit || 'kgf/cm²') : '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Espessura Nominal / Sobre-espessura:</span><span class="eq-field-val">${this.formatMeasurement(payloadObj.nominalThickness)} / ${this.formatMeasurement(payloadObj.corrosionAllowance)}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Norma de Fabricação:</span><span class="eq-field-val">${payloadObj.fabricationStandard || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Desenho de Projeto / Fluxograma:</span><span class="eq-field-val">${payloadObj.projectDrawingFlowchart || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">Norma de Inspeção em Serviço:</span><span class="eq-field-val">${payloadObj.inServiceInspectionCode || '-'}</span></div>
              <div class="eq-field-row"><span class="eq-field-label">NR-13 Aplicabilidade / Categoria:</span><span class="eq-field-val">${payloadObj.nr13Applicability || '-'} (${payloadObj.nr13Category || 'N/A'})</span></div>
              ${payloadObj.lineNumber ? `
                <div class="eq-field-row"><span class="eq-field-label">Tubulação Linha / DE / PARA:</span><span class="eq-field-val">${payloadObj.lineNumber} (${payloadObj.fromReference || '-'} ➔ ${payloadObj.toReference || '-'})</span></div>
              ` : ''}
            </div>

            <pre style="background: var(--color-bg-primary); border: 1px solid #27272a; border-radius: 6px; padding: 12px; font-size: 0.75rem; color: var(--color-text-disabled); max-height: 200px; overflow: auto;">${JSON.stringify(payloadObj, null, 2)}</pre>
          </div>

          <div style="padding: 12px 20px; border-top: 1px solid #27272a; display: flex; justify-content: flex-end;">
            <button id="btn-dismiss-snap-modal" class="btn-action btn-action-back">Fechar</button>
          </div>
        </div>
      </div>
    `;

    createIcons({ icons: { History }, nameAttr: "data-lucide", root: modalContainer });
    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-snap-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-dismiss-snap-modal')?.addEventListener('click', closeModal);
  }

  private openLinkDocModal() {
    const modalContainer = this.querySelector('#detail-modal-container') as HTMLElement;
    if (!modalContainer || !this.equipment) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 500px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="file-text" style="color: var(--ia-info-fg, #60a5fa); width: 20px; height: 20px;"></i> Vincular Documento Técnico
            </h3>
            <button id="btn-close-doc-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✖</button>
          </div>

          <form id="form-link-doc" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Título do Documento *</label>
              <input type="text" name="title" required placeholder="Ex: Desenho Isométrico da Linha 01" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tipo de Documento</label>
                <select name="documentType" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <option value="DRAWING">Desenho Técnico</option>
                  <option value="DATASHEET">Folha de Dados (Data Sheet)</option>
                  <option value="ISOMETRIC">Isométrico</option>
                  <option value="PID">Fluxograma P&ID</option>
                  <option value="CERTIFICATE">Certificado de Material</option>
                  <option value="REPORT">Relatório de Inspeção</option>
                  <option value="PROCEDURE">Procedimento</option>
                  <option value="OTHER">Outro</option>
                </select>
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nome do Arquivo / Referência</label>
                <input type="text" name="filename" required placeholder="Ex: ISO-TB-001-REV-0.pdf" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Notas Adicionais</label>
              <textarea name="notes" rows="2" placeholder="Observações..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-doc-modal" class="btn-action btn-action-back">Cancelar</button>
              <button type="submit" class="btn-action" style="background: var(--color-primary); color: var(--color-text-primary);">Salvar Documento</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({ icons: { FileText }, nameAttr: "data-lucide", root: modalContainer });
    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-doc-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-doc-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-link-doc') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const newDoc: EquipmentDocument = {
        id: `DOC-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        clientId: (contextStore.get().activeClientId || '') as any,
        equipmentId: this.equipment!.id,
        title: formData.get('title') as string,
        documentType: formData.get('documentType') as string,
        filename: formData.get('filename') as string,
        uploadedAt: new Date().toISOString(),
        uploadedBy: contextStore.get().userName || 'Usuário Técnico',
        ...((formData.get('notes') as string) ? { notes: (formData.get('notes') as string) } : {})
      };

      try {
        const ctx = sessionRequestContext();
        await window.app.platform.equipmentService.linkDocument(ctx, newDoc);
        closeModal();
        this.loadEquipmentData();
      } catch (err: any) {
        alert(`Erro ao vincular documento: ${err.message}`);
      }
    });
  }

  private async openEditModal() {
    // Navigate or dispatch to open edit modal from equipment-list or render inline full editor
    // Here we provide the complete multi-tab edit experience directly in the details page!
    const modalContainer = this.querySelector('#detail-modal-container') as HTMLElement;
    if (!modalContainer || !this.equipment) return;
    const eq = this.equipment;
    const isPiping = eq.equipmentType === 'PIPING_LINE';
    const pipingSystems = this.relatedEquipments.filter(e => e.equipmentType === 'PIPING_SYSTEM' && e.status !== 'INACTIVE' && e.id !== eq.id);

    const client = this.clients.find(c => c.id === eq.clientId);
    const clientName = client ? (client.name || client.legalName) : (eq.clientId || 'Cliente não identificado');
    const clientProjects = this.projects.filter(p => p.clientId === eq.clientId && p.status !== 'CANCELLED');
    const currentProjectId = eq.projectId || (this.unitAreas.find(u => u.id === (eq.unitAreaId || eq.areaId))?.projectId) || (clientProjects[0]?.id || '');
    const projectUnits = this.unitAreas.filter(u => u.projectId === currentProjectId && u.status !== 'INACTIVE');
    const currentUnitAreaId = eq.unitAreaId || eq.areaId || (projectUnits[0]?.id || '');
    
    const pipingEngineeringHtml = isPiping ? await PipingFormBuilder.renderPipingFields(eq, pipingSystems) : '';

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 800px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="edit-3" style="color: var(--ia-info-fg, #60a5fa); width: 20px; height: 20px;"></i> Editar Ativo Técnico (${eq.tag})
            </h3>
            <button id="btn-close-edit-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✖</button>
          </div>

          <form id="form-edit-details" style="padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px;">
            <!-- HIERARQUIA OBRIGATÓRIA: CLIENTE -> PROJETO -> UNIDADE/ÁREA -->
            <div style="background: rgba(37, 99, 235, 0.08); border: 1px solid rgba(59, 130, 246, 0.25); border-radius: 6px; padding: 12px; margin-bottom: 4px;">
              <div style="font-size: 0.78rem; font-weight: 700; color: var(--color-border-default); margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.05em; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="git-branch" style="width: 14px; height: 14px;"></i> Hierarquia Organizacional Obrigatória: Cliente &rarr; Projeto &rarr; Unidade/área
              </div>

              <div style="margin-bottom: 10px;">
                <label style="display: block; font-size: 0.8rem; color: var(--color-graphite-300); margin-bottom: 4px; font-weight: 600;">1. Cliente</label>
                <div style="width: 100%; background: var(--color-input-bg); border: 1px solid #27272a; border-radius: 6px; padding: 8px 12px; color: var(--color-border-default); font-weight: 600;">
                  <i data-lucide="building-2" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${clientName}
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-graphite-300); margin-bottom: 4px; font-weight: 600;">2. Projeto Vinculado *</label>
                  <select name="projectId" id="detail-edit-project-select" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="">Selecione o Projeto...</option>
                    ${clientProjects.map(p => `<option value="${p.id}" ${p.id === currentProjectId ? 'selected' : ''}>${p.code} - ${p.name}</option>`).join('')}
                  </select>
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-graphite-300); margin-bottom: 4px; font-weight: 600;">3. Unidade / Área do Projeto *</label>
                  <select name="unitAreaId" id="detail-edit-unitarea-select" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="">Selecione a Unidade / Área...</option>
                    ${projectUnits.map(u => `<option value="${u.id}" ${u.id === currentUnitAreaId ? 'selected' : ''}>${u.code} - ${u.name}</option>`).join('')}
                  </select>
                </div>
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">TAG do Ativo *</label>
                <input type="text" name="tag" required value="${eq.tag}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nome / Descrição *</label>
                <input type="text" name="name" required value="${eq.name}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <!-- Dados de Projeto e Operação separados -->
            <div style="border-top: 1px solid #27272a; padding-top: 12px;">
              <strong style="font-size: 0.85rem; color: var(--ia-info-fg, #60a5fa); display: block; margin-bottom: 8px;">DADOS DE PROJETO (DESIGN)</strong>
              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px;">
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Pressão de Projeto (bar/MPa)</label>
                  <input type="number" step="any" name="designPressureValue" value="${eq.designPressure?.value ?? ''}" placeholder="Ex: 15.0" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Temperatura de Projeto (°C)</label>
                  <input type="number" step="any" name="designTemperatureValue" value="${eq.designTemperature?.value ?? ''}" placeholder="Ex: 120" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">PMTA</label>
                  <div style="display: flex; gap: 4px;">
                    <input type="number" step="any" name="pmtaValue" value="${eq.pmtaValue ?? ''}" placeholder="Ex: 10" style="flex: 2; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                    <select name="pmtaUnit" style="flex: 1; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                      <option value="kgf/cm²" ${eq.pmtaUnit === 'kgf/cm²' ? 'selected' : ''}>kgf/cm²</option>
                      <option value="bar" ${eq.pmtaUnit === 'bar' ? 'selected' : ''}>bar</option>
                      <option value="MPa" ${eq.pmtaUnit === 'MPa' ? 'selected' : ''}>MPa</option>
                      <option value="psi" ${eq.pmtaUnit === 'psi' ? 'selected' : ''}>psi</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style="display: flex; justify-content: space-between; align-items: center; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Código de Projeto ${eq.equipmentType === 'PIPING_LINE' ? '<i data-lucide="lock" style="width:12px; height:12px;"></i>' : ''}</label>
                  <input type="text" name="projectCode" id="detail-project-code" value="${eq.projectCode || ''}" ${eq.equipmentType === 'PIPING_LINE' ? 'readonly style="width: 100%; background: rgba(255,255,255,0.05); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-disabled); cursor: not-allowed;"' : 'placeholder="Ex: ASME B31.3" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);"'}>
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Norma de Fabricação</label>
                  <input type="text" name="fabricationStandard" value="${eq.fabricationStandard || ''}" placeholder="Ex: ASME B31.3 / ASME VIII" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-top: 10px;">
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Espessura Nominal (mm)</label>
                  <input type="number" step="any" name="nominalThicknessValue" value="${eq.nominalThickness?.value ?? ''}" placeholder="Ex: 9.52" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Sobre-espessura Corrosão (mm)</label>
                  <input type="number" step="any" name="corrosionAllowanceValue" value="${eq.corrosionAllowance?.value ?? ''}" placeholder="Ex: 3.0" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Material de Fabricação</label>
                  <input type="text" name="material" value="${eq.material || ''}" placeholder="Ex: ASTM A106 Gr. B" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 10px;">
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Desenho de Referência</label>
                  <input type="text" name="drawingReference" value="${eq.drawingReference ?? eq.projectDrawingFlowchart ?? ''}" placeholder="Ex: DWG-3201-001" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Fluxograma (P&ID/PFD)</label>
                  <input type="text" name="flowDiagramReference" value="${eq.flowDiagramReference ?? eq.projectDrawingFlowchart ?? ''}" placeholder="Ex: P&ID-3201-001" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
              </div>
            </div>

            <!-- Dados de Operação separados -->
            <div style="border-top: 1px solid #27272a; padding-top: 12px;">
              <strong style="font-size: 0.85rem; color: var(--status-success-text); display: block; margin-bottom: 8px;">DADOS DE OPERAÇÃO (OPERATING)</strong>
              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px;">
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Pressão de Operação (bar/MPa)</label>
                  <input type="number" step="any" name="operatingPressureValue" value="${eq.operatingPressure?.value ?? ''}" placeholder="Ex: 10.5" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Temperatura de Operação (°C)</label>
                  <input type="number" step="any" name="operatingTemperatureValue" value="${eq.operatingTemperature?.value ?? ''}" placeholder="Ex: 65" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Fluido / Produto</label>
                  <input type="text" name="fluid" value="${eq.fluid || eq.service || ''}" placeholder="Ex: Óleo Cru, Amônia" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Código do Fluido</label>
                  <input type="text" name="fluidCode" value="${eq.fluidCode || ''}" placeholder="Ex: GC" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
              </div>
            </div>

            <!-- Seção Inspeção em Serviço & NR-13 -->
            <div style="border-top: 1px solid #27272a; padding-top: 12px;">
              <strong style="font-size: 0.85rem; color: var(--status-warning-text); display: block; margin-bottom: 8px;">INSPEÇÃO EM SERVIÇO & NR-13</strong>
              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px;">
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Norma Inspeção em Serviço</label>
                  <input type="text" name="inServiceInspectionCode" value="${eq.inServiceInspectionCode || ''}" placeholder="Ex: API 570, API 510" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Aplicabilidade NR-13</label>
                  <select name="nr13Applicability" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                    <option value="APPLICABLE" ${eq.nr13Applicability === 'APPLICABLE' ? 'selected' : ''}>Aplicável</option>
                    <option value="NOT_APPLICABLE" ${eq.nr13Applicability === 'NOT_APPLICABLE' ? 'selected' : ''}>Não Aplicável</option>
                    <option value="UNCLASSIFIED" ${eq.nr13Applicability === 'UNCLASSIFIED' ? 'selected' : ''}>Não Classificado</option>
                  </select>
                </div>
                <div>
                  <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Categoria NR-13</label>
                  <select name="nr13Category" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 10px; color: var(--color-text-primary);">
                    <option value="">(Selecione)</option>
                    <option value="I" ${eq.nr13Category === 'I' ? 'selected' : ''}>Categoria I</option>
                    <option value="II" ${eq.nr13Category === 'II' ? 'selected' : ''}>Categoria II</option>
                    <option value="III" ${eq.nr13Category === 'III' ? 'selected' : ''}>Categoria III</option>
                    <option value="IV" ${eq.nr13Category === 'IV' ? 'selected' : ''}>Categoria IV</option>
                    <option value="V" ${eq.nr13Category === 'V' ? 'selected' : ''}>Categoria V</option>
                  </select>
                </div>
              </div>
            </div>

            ${pipingEngineeringHtml}

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Observações</label>
              <textarea name="notes" rows="2" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;">${eq.notes || ''}</textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-edit" class="btn-action btn-action-back">Cancelar</button>
              <button type="submit" class="btn-action" style="background: var(--color-primary); color: var(--color-text-primary);">Salvar Alterações</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({ icons: { Edit3, GitBranch }, nameAttr: "data-lucide", root: modalContainer });
    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-edit-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-edit')?.addEventListener('click', closeModal);

    if (isPiping) {
      PipingFormBuilder.bindPipingEvents(modalContainer);
    }

    const projectSelect = modalContainer.querySelector('#detail-edit-project-select') as HTMLSelectElement;
    const unitAreaSelect = modalContainer.querySelector('#detail-edit-unitarea-select') as HTMLSelectElement;

    projectSelect?.addEventListener('change', () => {
      const pId = projectSelect.value;
      if (!pId) {
        if (unitAreaSelect) {
          unitAreaSelect.innerHTML = '<option value="">Selecione o Projeto primeiro...</option>';
          unitAreaSelect.disabled = true;
          unitAreaSelect.style.opacity = '0.6';
        }
        return;
      }
      const units = this.unitAreas.filter(u => u.projectId === pId && u.status !== 'INACTIVE');
      if (unitAreaSelect) {
        unitAreaSelect.disabled = false;
        unitAreaSelect.style.opacity = '1';
        if (units.length > 0) {
          unitAreaSelect.innerHTML = '<option value="">Selecione a Unidade / Área...</option>' +
            units.map(u => `<option value="${u.id}">${u.code} - ${u.name}</option>`).join('');
        } else {
          unitAreaSelect.innerHTML = '<option value="">Nenhuma unidade cadastrada neste projeto</option>';
        }
      }
    });

    const form = modalContainer.querySelector('#form-edit-details') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);

      const projectId = (formData.get('projectId') as string)?.trim();
      const unitAreaId = (formData.get('unitAreaId') as string)?.trim();

      if (!projectId) {
        alert("Obrigatório: Selecione o Projeto ao qual este equipamento pertence.");
        return;
      }
      if (!unitAreaId) {
        alert("Obrigatório: Selecione a Unidade / Área à qual este equipamento pertence.");
        return;
      }

      const parseNumVal = (field: string, unit: string) => {
        const str = formData.get(field) as string;
        if (!str || str.trim() === '') return null;
        const num = Number(str.replace(',', '.').trim());
        if (Number.isNaN(num) || !Number.isFinite(num)) {
          throw new Error(`Valor numérico inválido para o campo.`);
        }
        return { value: num, unit };
      };

      try {
        const designPress = parseNumVal('designPressureValue', 'bar');
        const operPress = parseNumVal('operatingPressureValue', 'bar');
        const designTemp = parseNumVal('designTemperatureValue', '°C'); 
        const pmtaValStr = (formData.get('pmtaValue') as string)?.trim();
        const pmtaValue = pmtaValStr ? parseFloat(pmtaValStr) : null;
        const pmtaUnit = (formData.get('pmtaUnit') as string) || null;
        const operTemp = parseNumVal('operatingTemperatureValue', '°C');
        const nomThk = parseNumVal('nominalThicknessValue', 'mm');
        const corrAllow = parseNumVal('corrosionAllowanceValue', 'mm');

        const isPipingEq = this.equipment?.equipmentType === 'PIPING_LINE';
        
        let pipingData: any = {};
        if (isPipingEq) {
          const innerDiameterStr = formData.get('calculatedInnerDiameter') as string;
          pipingData = {
            nps: (formData.get('nps') as string) || null,
            schedule: (formData.get('schedule') as string) || null,
            outsideDiameter: formData.get('outsideDiameter') ? { value: Number(formData.get('outsideDiameter')), unit: 'mm' } : null,
            insulation: (formData.get('insulation') as string) || null,
            spec: (formData.get('spec') as string) || null,
            pressureClass: (formData.get('pressureClass') as string) || null,
            materialFamily: (formData.get('materialFamily') as string) || null,
            materialSpecification: (formData.get('materialSpecification') as string) || null,
            materialGrade: (formData.get('materialGrade') as string) || null,
            dimensionalStandard: (formData.get('dimensionalStandard') as string) || null,
            calculatedInnerDiameter: innerDiameterStr ? { value: parseFloat(innerDiameterStr), unit: 'mm' } : null,
            dimensionSource: (formData.get('dimensionSource') as string) || 'CATALOG',
            dimensionManualSource: (formData.get('dimensionManualSource') as string) || null,
            dimensionManualJustification: (formData.get('dimensionManualJustification') as string) || null,
            fromReference: (formData.get('fromReference') as string) || null,
            toReference: (formData.get('toReference') as string) || null,
            nominalThickness: formData.get('nominalThickness') ? { value: Number(formData.get('nominalThickness')), unit: 'mm' } : null,
            fabricationCode: (formData.get('fabricationCode') as string) || null,
          };
        }

        const payload = JSON.parse(this.equipment?.payloadJson || '{}');
        payload.design = payload.design || {};
        payload.design.nominalThickness = isPipingEq ? pipingData.nominalThickness : nomThk;
        payload.design.projectCode = (formData.get('projectCode') as string) || null;
        payload.design.fabricationStandard = (formData.get('fabricationStandard') as string) || null;

        const updated: Equipment = {
          ...this.equipment!,
          projectId: projectId as any,
          unitAreaId: unitAreaId as any,
          areaId: unitAreaId as any,
          tag: (formData.get('tag') as string).trim().toUpperCase(),
          name: formData.get('name') as string,
          material: (formData.get('material') as string) || null,
          fabricationStandard: (formData.get('fabricationStandard') as string) || null,
          projectCode: (formData.get('projectCode') as string) || null,
          drawingReference: (formData.get('drawingReference') as string)?.trim() || null,
          flowDiagramReference: (formData.get('flowDiagramReference') as string)?.trim() || null,
          inServiceInspectionCode: (formData.get('inServiceInspectionCode') as string) || null,
          fluid: (formData.get('fluid') as string) || null,
          fluidCode: (formData.get('fluidCode') as string)?.trim() || null,
          designPressure: designPress,
          operatingPressure: operPress,
          designTemperature: designTemp, 
          pmtaValue: pmtaValue,
          pmtaUnit: pmtaUnit,
          operatingTemperature: operTemp,
          nominalThickness: isPipingEq ? pipingData.nominalThickness : nomThk,
          corrosionAllowance: corrAllow,
          nr13Applicability: (formData.get('nr13Applicability') as any) || 'UNCLASSIFIED',
          nr13Category: (formData.get('nr13Category') as string) || null,
          fromEquipmentId: (formData.get('fromEquipmentId') as any) || null,
          toEquipmentId: (formData.get('toEquipmentId') as any) || null,
          fluidClass: (formData.get('fluidClass') as string) || null,
          notes: (formData.get('notes') as string) || null,
          payloadJson: JSON.stringify(payload),

          ...(isPipingEq ? {
            nps: pipingData.nps,
            schedule: pipingData.schedule,
            outsideDiameter: pipingData.outsideDiameter,
            spec: pipingData.spec,
            pressureClass: pipingData.pressureClass,
            materialFamily: pipingData.materialFamily,
            materialSpecification: pipingData.materialSpecification,
            materialGrade: pipingData.materialGrade,
            dimensionalStandard: pipingData.dimensionalStandard,
            calculatedInnerDiameter: pipingData.calculatedInnerDiameter,
            dimensionSource: pipingData.dimensionSource,
            dimensionManualSource: pipingData.dimensionManualSource,
            dimensionManualJustification: pipingData.dimensionManualJustification,
            fromReference: pipingData.fromReference,
            toReference: pipingData.toReference,
            fabricationCode: pipingData.fabricationCode
          } : {})
        };

        const ctx = sessionRequestContext();
        await window.app.platform.equipmentService.updateEquipment(ctx, updated);
        closeModal();
        this.loadEquipmentData();
      } catch (err: any) {
        alert(err.message);
      }
    });
  }

  private openInactivateModal() {
    const modalContainer = this.querySelector('#detail-modal-container') as HTMLElement;
    if (!modalContainer || !this.equipment) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 480px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-warning-text); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="power" style="width: 20px; height: 20px;"></i> Inativar Equipamento (${this.equipment.tag})
            </h3>
            <button id="btn-close-inact-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✖</button>
          </div>

          <form id="form-inact-detail" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <p style="margin: 0; font-size: 0.85rem; color: var(--color-text-primary);">
              A inativação preservará integralmente todos os laudos, históricos técnicos e inspeções associadas a este ativo.
            </p>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Justificativa Obrigatória *</label>
              <textarea name="reason" required minlength="3" rows="3" placeholder="Informe o motivo da inativação técnica..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-inact" class="btn-action btn-action-back">Cancelar</button>
              <button type="submit" class="btn-action" style="background: var(--status-warning-text); color: var(--color-text-inverse);">Confirmar Inativação</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({ icons: { Power }, nameAttr: "data-lucide", root: modalContainer });
    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-inact-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-inact')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-inact-detail') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const reason = (form.querySelector('textarea[name="reason"]') as HTMLTextAreaElement).value;
      try {
        const ctx = sessionRequestContext();
        await window.app.platform.equipmentService.inactivateEquipment(ctx, this.equipment!.id, reason);
        closeModal();
        this.loadEquipmentData();
      } catch (err: any) {
        alert(err.message);
      }
    });
  }

  private async openDeleteModal() {
    const modalContainer = this.querySelector('#detail-modal-container') as HTMLElement;
    if (!modalContainer || !this.equipment) return;
    const eq = this.equipment;

    const ctx = { activeClientId: contextStore.get().activeClientId, permissions: contextStore.get().permissions, correlationId: '', userId: contextStore.get().userId } as any;

    let eligibility: any;
    try {
      eligibility = await window.app.platform.equipmentService.checkDeleteEligibility(ctx, eq.id);
    } catch (err: any) {
      alert(err.safeMessage || err.message);
      return;
    }

    if (!eligibility.canDelete || eligibility.hasReport) {
      modalContainer.innerHTML = `
        <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
          <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 500px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
            <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
              <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-warning-solid); display: flex; align-items: center; gap: 8px;">
                <i data-lucide="alert-triangle" style="width: 20px; height: 20px;"></i> NÃO É POSSÍVEL EXCLUIR ESTE EQUIPAMENTO
              </h3>
              <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✖</button>
            </div>
            
            <div style="padding: 20px; color: var(--color-text-primary); font-size: 0.95rem; line-height: 1.5;">
              <p style="margin-top: 0;">${eligibility.blockReason || 'Este equipamento possui Relatório de Integridade vinculado e faz parte do histórico documental do INSPECT IA.'}</p>
              <p style="margin-bottom: 0;">Utilize a opção de inativação/arquivamento, quando disponível.</p>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding: 16px 20px;">
              <button type="button" id="btn-ok-modal" class="btn-action" style="background: var(--color-primary); color: var(--color-text-primary);">OK</button>
            </div>
          </div>
        </div>
      `;
      createIcons({ icons: { AlertTriangle }, nameAttr: "data-lucide", root: modalContainer });
      const closeModal = () => { modalContainer.innerHTML = ''; };
      modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
      modalContainer.querySelector('#btn-ok-modal')?.addEventListener('click', closeModal);
      return;
    }

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 500px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-danger-solid); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="trash-2" style="width: 20px; height: 20px;"></i> EXCLUIR EQUIPAMENTO?
            </h3>
            <button id="btn-close-del-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✖</button>
          </div>

          <form id="form-del-detail" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <div style="color: var(--color-text-primary); font-size: 0.95rem;">
              <p style="margin-top: 0;">Você está prestes a excluir:</p>
              <ul style="list-style: none; padding: 0; margin-bottom: 10px; background: var(--color-input-bg); padding: 12px; border-radius: 6px;">
                <li><strong>TAG:</strong> ${eq.tag}</li>
                ${eq.name ? `<li><strong>DESCRIÇÃO:</strong> ${eq.name}</li>` : ''}
                <li><strong>TIPO:</strong> ${eq.equipmentType || '-'}</li>
              </ul>
              <p style="margin-bottom: 0; color: var(--status-danger-solid); font-weight: bold;">Esta ação não poderá ser desfeita.</p>
            </div>

            ${eligibility.hasDependencies ? `
            <div style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 6px; padding: 12px; color: var(--status-danger-border); font-size: 0.85rem;">
              <strong>Atenção:</strong> Este equipamento possui dados de inspeção em andamento, recomendações ou anexos. Confirme o cancelamento/exclusão desses registros antes de excluir o equipamento.
            </div>
            ` : ''}

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Motivo / Justificativa da Exclusão *</label>
              <textarea name="reason" required minlength="3" rows="3" placeholder="Informe a razão da exclusão cadastral..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-del" class="btn-action btn-action-back">Cancelar</button>
              <button type="submit" class="btn-action" style="background: var(--status-danger-solid); color: var(--color-text-primary);">Confirmar Exclusão</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({ icons: { Trash2 }, nameAttr: "data-lucide", root: modalContainer });
    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-del-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-del')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-del-detail') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const reason = (form.querySelector('textarea[name="reason"]') as HTMLTextAreaElement).value;
      try {
        const res = await window.app.platform.equipmentService.deleteEquipment(ctx, eq.id, reason);
        closeModal();
        router.navigate('/equipment');
      } catch (err: any) {
        alert(err.safeMessage || err.message);
      }
    });
  }
}

if (!customElements.get("app-equipment-detail-page")) {
  customElements.define("app-equipment-detail-page", AppEquipmentDetailPage);
}



